import type { MPAInterceptedResponse } from '@repo/api-gw-sdk';

import { isMPAInterceptedResponse } from './mpaIntercept';

interface MpaResponseBody {
  json: () => Promise<unknown>;
  text: () => Promise<string>;
  binary: () => Promise<Blob>;
}

interface MpaResponseContext {
  httpStatusCode: number;
  body: MpaResponseBody;
}

interface MpaMiddleware<
  RequestContext,
  ResponseContext extends MpaResponseContext,
> {
  pre: (context: RequestContext) => Promise<RequestContext>;
  post: (context: ResponseContext) => Promise<ResponseContext>;
}

export const createMpaMiddleware = <
  RequestContext,
  ResponseContext extends MpaResponseContext,
>(
  onMpaIntercepted: (payload: MPAInterceptedResponse) => Promise<void>
): MpaMiddleware<RequestContext, ResponseContext> => ({
  pre: async (context) => context,
  post: async (context) => {
    if (context.httpStatusCode !== 201) {
      return context;
    }

    // The body stream can only be read once. To peek at the JSON without
    // breaking the downstream response processor on non-MPA 201s, consume it
    // here and serve all three accessors from the cached value.
    const cachedJson = context.body.json();
    const cachedText = cachedJson.then((j) => JSON.stringify(j));
    context.body = {
      json: () => cachedJson,
      text: () => cachedText,
      binary: () => cachedText.then((t) => new Blob([t])),
    };

    const body = await cachedJson;
    if (!isMPAInterceptedResponse(body)) {
      return context;
    }

    // Hand the dialog flow control. It resolves once the user has approved
    // and the submit-for-approval request has succeeded, or rejects on
    // cancellation / submit failure.
    await onMpaIntercepted(body);
    return context;
  },
});
